import React from 'react';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { jsPDF } from 'jspdf';
import { downloadXML, downloadFile } from 'src/util/util';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import html2canvas from 'html2canvas';
import { ThemeContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  usePedigreeInstance,
  nsId
} from '../../../pedigree-tool/pedigree/instanceContext';
import CanRiskModal from './CanRiskModal';
import { downloadCanRisk } from './canriskDownload';
import ExportOptionList from './ExportOptionList';
import { exportNotesOf } from './exportNotes';
import { computeExportHeader, computeExportCanvasSize } from './exportLayout';
import {
  hideDataOverlaysForExport,
  stripDataOverlaysFromClone
} from '../../../pedigree-tool/data-overlays/exportGuard';

// Fetch Inter from Google Fonts and embed as base64 inside the SVG so the
// exported PNG/PDF uses the same font as the app (web fonts are not available
// in serialised SVG image contexts due to browser security restrictions).
const buildEmbeddedFontStyle = async () => {
  try {
    // Include the italic axis (ital,wght) so per-label italic / bold-italic
    // formatting renders in exported PNG/PDF — upright weights alone can't show
    // a font-style:italic <tspan>. The fetch loop below embeds every face the
    // CSS references, so the extra italic faces are picked up automatically.
    const cssResp = await fetch(
      'https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,700&display=swap'
    );
    const cssText = await cssResp.text();
    const urls = [...cssText.matchAll(/url\((https:\/\/[^)]+)\)/g)].map(
      (m) => m[1]
    );
    let result = cssText;
    for (const url of urls) {
      const resp = await fetch(url);
      const buf = await resp.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      result = result.replace(url, `data:font/woff2;base64,${btoa(binary)}`);
    }
    return result;
  } catch {
    return null;
  }
};

export default function ExportMenu(props) {
  const t = useAutoTranslation();

  const { user } = props;
  const { _id, familyId } = user;
  const { setSnack } = React.useContext(SnackContext);
  const { theme } = React.useContext(ThemeContext);
  const { content } = React.useContext(AppContext);
  const [canRiskModalOpen, setCanRiskModalOpen] = React.useState(false);

  // Multi-pedigree-tabs: scope element lookups to THIS chart's container so, with
  // several charts mounted, "Export" captures the visible chart's SVG/legend/dates
  // rather than whichever matching id happens to be first in the document. With a
  // lone chart (no container / no instanceId) it uses the original document lookups
  // by id, so single-pedigree export is unchanged.
  const { instanceId, containerRef } = usePedigreeInstance();

  const exportImage = async (export_pdf = false) => {
    const root = containerRef?.current || null;
    const svgElement = root
      ? root.querySelector('svg.pedigree-chart-svg')
      : document.getElementById('pedigree-chart');
    const byId = (id) =>
      root ? root.querySelector(`[id="${id}"]`) : document.getElementById(id);
    const diagnosesLegend = byId('diagnoses-legend');
    const pedigreeIdentifier = byId('pedigree-identifier');
    // TGCLOUD-1791 — rendered only when the preset switches the family note on
    // AND the family has one, so an absent element means "no note to print".
    const pedigreeFamilyNote = byId('pedigree-family-note');
    const pedigreeDates = byId('pedigree-dates');

    const padding = 20;
    const backgroundColor =
      theme.appearance.value === 'light' ? 'white' : theme.appearance.shade;

    const captureElement = async (element, options = {}) => {
      if (!element) return null;
      const canvas = await html2canvas(element, {
        backgroundColor,
        scale: 2,
        useCORS: true,
        ...options
      });
      const image = new Image();
      image.src = canvas.toDataURL();
      await new Promise((res) => (image.onload = res));
      return image;
    };

    const diagnosesLegendImage = await captureElement(diagnosesLegend);
    const pedigreeIdentifierImage = await captureElement(pedigreeIdentifier);
    const pedigreeFamilyNoteImage = await captureElement(pedigreeFamilyNote);
    const pedigreeDatesImage = await captureElement(pedigreeDates);

    const showLogo = content?.settings?.pedigreeSetting?.showLogoOnExport;
    const logoElement = showLogo ? byId('pedigree-export-logo') : null;
    const logoImage = await captureElement(logoElement);

    // --- Full-diagram SVG export ---
    // Get the bounding box of ALL diagram content in the diagram group's local
    // coordinate space (independent of the current zoom/pan viewport).
    //
    // DATA OVERLAYS ARE HIDDEN ACROSS THIS WINDOW (TGCLOUD-1670). The exported
    // pedigree is the Bennett-standard document, so an overlay must leave no
    // trace in it — including in the FRAME, because the viewBox is measured from
    // the live .diagram bounding box and a ring extends past the node it marks.
    //
    // The window opens here, after the five awaited html2canvas captures above
    // (the family-note capture joined them in TGCLOUD-1791, deliberately on this
    // side of the window for the reason that follows):
    // those span real macrotasks, so a hide placed any earlier could be undone
    // by a timer or an image decode landing inside them. It closes immediately
    // after cloneNode, before the font fetch below, and contains no await.
    // try/finally, because the querySelector below has no null guard and a throw
    // would otherwise leave the overlay permanently invisible on screen.
    const restoreDataOverlays = hideDataOverlaysForExport(svgElement);
    let diagramBBox = null;
    let clonedSvg = null;
    try {
      const diagramGroup = svgElement.querySelector('.diagram');
      diagramBBox = diagramGroup ? diagramGroup.getBBox() : null;
      clonedSvg = svgElement.cloneNode(true);
    } finally {
      restoreDataOverlays();
    }
    stripDataOverlaysFromClone(clonedSvg);

    const svgPad = 60;

    let exportSvgWidth, exportSvgHeight, viewBox;

    if (diagramBBox && diagramBBox.width > 0 && diagramBBox.height > 0) {
      const vbX = diagramBBox.x - svgPad;
      const vbY = diagramBBox.y - svgPad;
      const vbW = diagramBBox.width + svgPad * 2;
      const vbH = diagramBBox.height + svgPad * 2;
      viewBox = `${vbX} ${vbY} ${vbW} ${vbH}`;

      // Render the pedigree at high resolution so users can zoom in on the PDF/PNG.
      // Target 4000 px wide; scale height to preserve the diagram aspect ratio.
      exportSvgWidth = 4000;
      exportSvgHeight = Math.round((vbH / vbW) * exportSvgWidth);
    } else {
      // Fallback: 4x the viewport width for high resolution
      exportSvgWidth = svgElement.clientWidth * 4;
      exportSvgHeight = svgElement.clientHeight * 4;
      viewBox = null;
    }

    // Stamp pixel dimensions and the full-diagram viewBox onto the clone taken
    // inside the overlay-hidden window above.
    clonedSvg.setAttribute('width', exportSvgWidth);
    clonedSvg.setAttribute('height', exportSvgHeight);
    if (viewBox) clonedSvg.setAttribute('viewBox', viewBox);

    // Reset zoom/pan transform so viewBox framing drives positioning
    const clonedDiagram = clonedSvg.querySelector('.diagram');
    if (clonedDiagram) clonedDiagram.setAttribute('transform', '');

    // Reset dot-grid patternTransform (it tracks the live zoom; not needed here).
    // The pattern id is namespaced per instance, so match the namespaced id.
    const dotGrid = clonedSvg.querySelector(
      `[id="${nsId('dot-grid', instanceId)}"]`
    );
    if (dotGrid) dotGrid.removeAttribute('patternTransform');

    // Embed Inter font so exported image uses the same font as the app.
    // CSS custom properties and web-font links don't work inside a serialised
    // SVG rendered as <img>, so we fetch the font and inline it as base64.
    const fontCSS = await buildEmbeddedFontStyle();
    if (fontCSS) {
      const styleEl = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'style'
      );
      styleEl.textContent = fontCSS;
      clonedSvg.insertBefore(styleEl, clonedSvg.firstChild);
    }
    clonedSvg.setAttribute(
      'font-family',
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif'
    );
    clonedSvg.style.fontFamily =
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif';

    // Serialize and base64-encode (unicode-safe)
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clonedSvg);
    const svgBase64 = btoa(unescape(encodeURIComponent(svgString)));

    // Layout: logo top-left, identifier below logo, SVG left column, legend
    // top-right, dates bottom-right. The geometry lives in exportLayout.js so it
    // can be tested without a canvas — see the header-stack bug documented there
    // (TGCLOUD-1759).
    const legendW = diagnosesLegendImage?.width || 0;
    const legendH = diagnosesLegendImage?.height || 0;
    const datesW = pedigreeDatesImage?.width || 0;
    const datesH = pedigreeDatesImage?.height || 0;

    const { logoY, identifierY, familyNoteY, contentY } = computeExportHeader({
      padding,
      logoHeight: logoImage?.height || 0,
      identifierHeight: pedigreeIdentifierImage?.height || 0,
      familyNoteHeight: pedigreeFamilyNoteImage?.height || 0
    });

    const { width: canvasWidth, height: canvasHeight } =
      computeExportCanvasSize({
        padding,
        svgWidth: exportSvgWidth,
        svgHeight: exportSvgHeight,
        identifierWidth: pedigreeIdentifierImage?.width || 0,
        familyNoteWidth: pedigreeFamilyNoteImage?.width || 0,
        legendWidth: legendW,
        legendHeight: legendH,
        datesHeight: datesH,
        contentY
      });

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Logo — top left
    if (logoImage) {
      ctx.drawImage(logoImage, padding, logoY);
    }

    // Identifier — below the logo (or at the top when there is no logo)
    if (pedigreeIdentifierImage) {
      ctx.drawImage(pedigreeIdentifierImage, padding, identifierY);
    }

    // Family note — below the identifier. computeExportHeader reserved this
    // position AND the height it takes, in the same change: a draw call added
    // without a matching entry in the header arithmetic is precisely the defect
    // documented in exportLayout.js, where the body started underneath a header
    // part the height calculation did not know about (TGCLOUD-1791/1759).
    if (pedigreeFamilyNoteImage) {
      ctx.drawImage(pedigreeFamilyNoteImage, padding, familyNoteY);
    }

    // SVG pedigree — left column
    const svgImage = new Image();
    svgImage.src = `data:image/svg+xml;base64,${svgBase64}`;
    await new Promise((resolve, reject) => {
      svgImage.onload = () => {
        ctx.drawImage(
          svgImage,
          padding,
          contentY,
          exportSvgWidth,
          exportSvgHeight
        );
        resolve();
      };
      svgImage.onerror = reject;
    });

    // Legend — top right
    if (diagnosesLegendImage) {
      const legendX = padding + exportSvgWidth + padding;
      const legendY = contentY;
      ctx.drawImage(diagnosesLegendImage, legendX, legendY);
    }

    // Dates — bottom right
    if (pedigreeDatesImage) {
      const x = canvas.width - datesW - padding;
      const y = canvas.height - datesH - padding;
      ctx.drawImage(pedigreeDatesImage, x, y);
    }

    const finalImage = canvas.toDataURL('image/png');
    if (export_pdf) {
      const pdf = new jsPDF({
        orientation: canvasWidth > canvasHeight ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvasWidth, canvasHeight]
      });
      pdf.addImage(finalImage, 'PNG', 0, 0, canvasWidth, canvasHeight);
      pdf.save(`${familyId}.pdf`);
    } else {
      const link = document.createElement('a');
      link.href = finalImage;
      link.download = `${familyId}.png`;
      link.click();
    }
  };

  const exportPDF = () => {
    //export image as pdf
    exportImage(true);
  };
  const getXMLData = () => {
    axios
      .get(APICONSTANTS.getXMLData.replace(':user_id', _id))
      .then((res) => {
        downloadXML(res.data, familyId);
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('Error exporting as XML data'),
          severity: 'error'
        });
      });
  };

  const getJsonData = () => {
    axios
      .get(APICONSTANTS.getGA4JsonData.replace(':user_id', _id))
      .then((res) => {
        downloadFile(JSON.stringify(res.data), familyId, 'json');
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('Error converting to JSON'),
          severity: 'error'
        });
      });
  };

  const getPhenopacketData = () => {
    axios
      .get(APICONSTANTS.getPhenopacketData.replace(':user_id', _id))
      .then((res) => {
        // Suffix the family reference so a Phenopacket does not land in the
        // downloads folder under the same name as the GA4GH Pedigree JSON
        // export — both are .json, and the earlier file was silently shadowed.
        downloadFile(
          JSON.stringify(res.data, null, 2),
          `${familyId}-phenopacket`,
          'json'
        );
        // What the export could not place is named in a header, since the
        // body is the file (TGCLOUD-1845).
        const notes = exportNotesOf(res.headers);
        if (notes.length) {
          setSnack({
            status: true,
            msg:
              notes.length > 3
                ? t('Phenopacket exported. {{notes}} …and {{count}} more.', {
                    notes: notes.slice(0, 3).join(' '),
                    count: notes.length - 3
                  })
                : t('Phenopacket exported. {{notes}}', {
                    notes: notes.join(' ')
                  }),
            severity: 'warning'
          });
        }
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('Error exporting Phenopacket'),
          severity: 'error'
        });
      });
  };

  const getCanRiskData = () => setCanRiskModalOpen(true); // NEW

  const handleCanRiskDownload = (allOptionsArray, targetId) =>
    downloadCanRisk({
      userId: _id,
      familyId,
      members: allOptionsArray,
      targetId,
      setSnack
    });

  // Formats grouped by what they are FOR, not by file type: an image to share or
  // print, a data file to hand to another system, or an input file for a risk
  // model. Adding a format is one entry here — a new risk tool joins the "Risk
  // tools" group, a new interchange format joins "Data & interoperability".
  //
  // `anchor` values are the Copilot walkthrough anchors (pedigree.export.*) and
  // must be preserved — the "Export or share the pedigree" walkthrough points at
  // the PNG, XML and CanRisk rows by these ids.
  const groups = [
    {
      id: 'images',
      caption: t('Images & documents'),
      options: [
        {
          id: 'png',
          label: t('PNG'),
          description: t('Chart image with legend and identifiers'),
          ext: '.png',
          icon: <ImageOutlinedIcon />,
          anchor: 'pedigree.export.png',
          onClick: () => exportImage()
        },
        {
          id: 'pdf',
          label: t('PDF'),
          description: t('The same chart, print-ready'),
          ext: '.pdf',
          icon: <PictureAsPdfOutlinedIcon />,
          anchor: 'pedigree.export.pdf',
          onClick: exportPDF
        }
      ]
    },
    {
      id: 'interchange',
      caption: t('Data & interoperability'),
      options: [
        {
          id: 'xml',
          label: 'XML',
          description: t('TrakGene pedigree exchange format'),
          ext: '.xml',
          icon: <CodeOutlinedIcon />,
          anchor: 'pedigree.export.xml',
          onClick: getXMLData
        },
        {
          id: 'json',
          label: 'JSON',
          description: t('GA4GH Pedigree format'),
          ext: '.json',
          icon: <DataObjectOutlinedIcon />,
          anchor: 'pedigree.export.json',
          onClick: getJsonData
        },
        {
          id: 'phenopacket',
          label: 'Phenopacket',
          description: t('GA4GH research profile'),
          ext: '.json',
          icon: <BiotechOutlinedIcon />,
          onClick: getPhenopacketData
        }
      ]
    },
    {
      id: 'risk',
      caption: t('Risk tools'),
      options: [
        {
          id: 'canrisk',
          label: 'CanRisk…',
          description: t('BOADICEA risk model input'),
          ext: '.txt',
          icon: <AssessmentOutlinedIcon />,
          anchor: 'pedigree.export.canrisk',
          onClick: getCanRiskData
        }
      ]
    }
  ];

  return (
    <>
      <ExportOptionList groups={groups} />
      <CanRiskModal
        data={props.data}
        open={canRiskModalOpen}
        onClose={() => setCanRiskModalOpen(false)}
        onDownload={handleCanRiskDownload}
        userId={_id}
      />
    </>
  );
}
